import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useEvent } from "@hooks/useEvent";
import { Text, Button } from "@fragments";
import { VideoCallRounded } from "@mui/icons-material";
import {
    CommunityAssetImageField,
    AssetCategory,
} from "@features/uploadAsset";
import { VideoUploadField } from "@features/videoUpload";
import AICircle from "@components/AICircle";
import GenerateVideoScriptModal from "@modals/Content/GenerateVideoScript";
import EVENTS from "../events";

export default function Video({
    communityId,
    bannerUrl,
    onBannerChange,
    onBannerUploadingChange,
    defaultAspectRatio,
    defaultVideoUrl,
    onVideoChange,
    onVideoSelected,
    onVideoUploadingChange,
    contentId,
    getContentName,
}) {
    const { t } = useTranslation("contentModals");
    const [aspectRatio, setAspectRatio] = useState(defaultAspectRatio || 1.78);
    const [videoUrl, setVideoUrl] = useState(defaultVideoUrl);
    const [uploading, setUploading] = useState(false);

    const REF_generateScriptModal = useRef();

    useEvent(EVENTS.ASPECT_RATIO_CHANGE, (ratio) => {
        setAspectRatio(ratio);
    });

    // The script generator reads the content's section and playlist on the
    // server, so it only exists for a saved content (one with an id) that has
    // no video yet.
    const canGenerateScript = !videoUrl && !uploading && !!contentId;

    function handleVideoChange(url, metadata) {
        setVideoUrl(url);
        onVideoChange(url, metadata);
    }

    function handleUploadingChange(value) {
        setUploading(value);
        onVideoUploadingChange?.(value);
    }

    function handleOpenGenerateScript() {
        REF_generateScriptModal.current?.open({
            id: contentId,
            name: getContentName?.() || "",
        });
    }

    return (
        <div>
            <Text.h3
                left={<VideoCallRounded />}
                value={t("common:fields.video")}
                className="mb-1 mt-4"
            />

            <div
                className="video-upload-area "
                style={{
                    width: aspectRatio > 1 ? "100%" : "fit-content",
                }}
            >
                <div className="banner-select-box">
                    {communityId ? (
                        <CommunityAssetImageField
                            communityId={communityId}
                            category={AssetCategory.CONTENT_BANNER}
                            value={bannerUrl}
                            onChange={onBannerChange}
                            onUploadingChange={onBannerUploadingChange}
                            size={aspectRatio > 1 ? 100 : 140}
                            aspectRatio={aspectRatio}
                            imageMaxWidth={1600}
                            title={t("manageContent.video.bannerTitle")}
                            displayTitle
                        />
                    ) : null}
                </div>
                <VideoUploadField
                    value={videoUrl}
                    onChange={handleVideoChange}
                    onUploadingChange={handleUploadingChange}
                    onFileSelected={onVideoSelected}
                    aspectRatio={aspectRatio}
                />
            </div>

            {canGenerateScript && (
                <div className="video-script-cta">
                    <Button
                        color="overlay"
                        size="small"
                        startIcon={<AICircle size={18} />}
                        onClick={handleOpenGenerateScript}
                        preventLoading
                    >
                        {t("manageContent.video.generateScript")}
                    </Button>
                    <Text.caption className="video-script-cta__hint">
                        {t("manageContent.video.generateScriptHint")}
                    </Text.caption>
                </div>
            )}

            {contentId ? <GenerateVideoScriptModal modalRef={REF_generateScriptModal} /> : null}
        </div>
    );
}
